<ng-container
  *ngIf="{
    showStepperModal: showStepperModal$ | async,
    isMobile: isMobile$ | async,
    sliderActiveIndex: sliderActiveIndex$ | async,
    isLastStepVisible: isLastStepVisible$ | async
  } as viewModel"
>
  <div
    class="dialog-container"
    [ngClass]="viewModel.showStepperModal ? 'stepper-modal' : 'wide-modal'"
  >
    <div class="prompt-panel">
      <!-- header that appear for bottom sheet -->
      <div *ngIf="viewModel.isMobile" class="non-desktop-header">
        <div class="title-text">
          {{ mobileTitle }}
        </div>
      </div>
      <!-- content body -->
      <div
        class="prompt-content-container"
        [style.padding-right.px]="viewModel.isMobile ? PADDING_X_BOT_SHEET : PADDING_X_WIDE_STEPPER"
        [style.padding-left.px]="viewModel.isMobile ? PADDING_X_BOT_SHEET : PADDING_X_WIDE_STEPPER"
      >
        <!-- arrow left -->
        <ng-container *ngIf="viewModel.showStepperModal && !viewModel.isMobile">
          <div class="nav-btn-container">
            <app-pencil-button
              class="pencil-button slider-button"
              [disabled]="viewModel.sliderActiveIndex === 0"
              [theme]="PencilButtonThemeEnum.PRIMARY_DARK"
              icon="arrow_backward"
              [iconConfig]="{ type: IconTypes.matIcon }"
              (clickEvent)="backHelperLayout()"
            ></app-pencil-button>
          </div>
        </ng-container>

        <!-- steps body-->
        <div class="prompt-content">
          <div *ngIf="!viewModel.isMobile" class="prompt-text">
            {{ desktopTitle }}
          </div>
          <div
            (touchstart)="onTouchStart($event)"
            (touchmove)="onTouchMove($event)"
            (touchend)="onTouchEnd($event)"
          >
            <div class="prompt-steps" [style.gap.px]="GAP_BETWEEN_STEPS" #slider>
              <div class="step-container" *ngFor="let step of steps; let i = index">
                <ng-container
                  *ngTemplateOutlet="
                    stepTemplate;
                    context: {
                      step: step,
                      index: i,
                      isLastStep: i === steps.length - 1
                    }
                  "
                ></ng-container>
              </div>
            </div>
          </div>
          <div *ngIf="viewModel.isMobile || viewModel.showStepperModal" class="dots-container">
            <span
              *ngFor="let step of steps; let i = index"
              class="dot"
              [class.active]="i === viewModel.sliderActiveIndex"
              (click)="showSpecificHelperLayout(i)"
            ></span>
          </div>
        </div>

        <!-- arrow right -->
        <ng-container *ngIf="viewModel.showStepperModal && !viewModel.isMobile">
          <div class="nav-btn-container">
            <app-pencil-button
              class="pencil-button slider-button"
              [disabled]="viewModel.sliderActiveIndex === steps.length - 1"
              [theme]="PencilButtonThemeEnum.PRIMARY_DARK"
              icon="arrow_forward"
              [iconConfig]="{ type: IconTypes.matIcon }"
              (clickEvent)="nextHelperLayout()"
            ></app-pencil-button>
          </div>
        </ng-container>
      </div>
      <div class="divider"></div>
      <!-- bottom buttons -->
      <div class="prompt-buttons">
        <div class="settings-button-container">
          <!-- settings button-->
          <app-pencil-button
            *ngIf="showDeviceSettingsButton"
            class="pencil-button settings-button"
            [label]="deviceSettingsButtonLabel"
            [theme]="PencilButtonThemeEnum.PRIMARY_DARK"
            icon="general_settings"
            [iconConfig]="{ type: IconTypes.svgIcon }"
            (clickEvent)="openDeviceSettings()"
            [supportLongLabel]="true"
          >
          </app-pencil-button>
        </div>

        <div class="action-buttons">
          <!-- loading button-->
          <app-pencil-button
            *ngIf="loading; else nextButton"
            class="pencil-button loading-button"
            [theme]="PencilButtonThemeEnum.PRIMARY_DARK"
            [label]="'Checking' | translate"
            [disabled]="true"
            [showSpinner]="true"
            [supportLongLabel]="true"
          ></app-pencil-button>

          <ng-template #nextButton>
            <!-- next button-->
            <app-pencil-button
              *ngIf="!viewModel.isLastStepVisible; else refreshButton"
              class="pencil-button next-button"
              [theme]="PencilButtonThemeEnum.PRIMARY"
              [label]="'Next' | translate"
              icon="arrow_forward"
              [iconConfig]="{ type: IconTypes.matIcon }"
              (clickEvent)="nextHelperLayout()"
              [supportLongLabel]="true"
            ></app-pencil-button>
          </ng-template>

          <ng-template #refreshButton>
            <!-- refresh button-->
            <app-pencil-button
              *ngIf="showRefreshButton && (!isPhysicalDesktop || isFirefox); else checkAgainButton"
              class="pencil-button refresh-button"
              [theme]="PencilButtonThemeEnum.PRIMARY"
              [label]="'Refresh to apply changes' | translate"
              icon="refresh"
              [iconConfig]="{ type: IconTypes.matIcon }"
              (clickEvent)="refresh()"
              [supportLongLabel]="true"
            ></app-pencil-button>
          </ng-template>

          <ng-template #checkAgainButton>
            <!-- check again button-->
            <div class="error-container" *ngIf="hasRelatedError; else doneButton">
              <div class="error-text">
                {{ errorMsg }}
              </div>

              <app-pencil-button
                class="pencil-button check-again-button"
                [theme]="PencilButtonThemeEnum.DANGER"
                [label]="'Check again?' | translate"
                icon="refresh"
                [iconConfig]="{ type: IconTypes.matIcon }"
                (clickEvent)="onDone()"
                [supportLongLabel]="true"
              ></app-pencil-button>
            </div>
          </ng-template>

          <ng-template #doneButton>
            <!-- done button-->
            <app-pencil-button
              class="pencil-button done-button"
              [theme]="PencilButtonThemeEnum.PRIMARY"
              [label]="'I’ve completed these steps' | translate"
              icon="arrow_forward"
              [iconConfig]="{ type: IconTypes.matIcon }"
              (clickEvent)="onDone()"
              [supportLongLabel]="true"
            ></app-pencil-button>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- step view template -->
    <ng-template #stepTemplate let-step="step" let-index="index" let-isLastStep="isLastStep">
      <div
        class="step"
        [ngStyle]="{
          width: STEP_WIDTH + 'px'
        }"
      >
        <div
          *ngIf="!isLastStep && !viewModel.isMobile && !viewModel.showStepperModal"
          class="divider-step"
        ></div>
        <div class="step-circle" [style.margin-top]="isLastStep && '-10px !important'">
          {{ index + 1 }}
        </div>
        <div class="step-text-container">
          <div class="inline-step-circle">
            {{ index + 1 }}
          </div>
          <div class="step-text" [innerHTML]="step.stepText | safeHtml"></div>
        </div>
        <img
          *ngIf="step.stepImgSrc"
          [src]="'assets/imgs/' + step.stepImgSrc"
          alt="{{ 'helper step' | translate }}"
        />
      </div>
    </ng-template>

    <!-- pushed to the end to appear on top of the modal without using z-index-->
    <app-pencil-button
      class="custom-btn-close"
      [ngClass]="{
        'size-20': !viewModel.isMobile,
        'size-24': viewModel.isMobile
      }"
      icon="close"
      [iconConfig]="{ type: IconTypes.matIcon }"
      (clickEvent)="onClose()"
    >
    </app-pencil-button>
  </div>
</ng-container>
